import React from 'react';

/* ─── field base style ─── */
const fieldBase = {
  background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 8,
  padding: '8px 11px', fontSize: 13.5, color: 'var(--text)', fontFamily: 'inherit',
  outline: 'none', width: '100%', transition: 'background .15s, border-color .15s, box-shadow .15s',
};
const onFocus = e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; e.target.style.background = 'var(--white)'; };
const onBlur = e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; e.target.style.background = 'var(--bg)'; };

/* ─── Badge ─── */
export const Badge = ({ status, children }) => {
  const map = {
    pending: { bg: 'var(--warning-bg)', color: 'var(--warning)', dot: 'var(--warning)' },
    active: { bg: 'var(--success-bg)', color: 'var(--success)', dot: 'var(--success)' },
    completed: { bg: 'var(--info-bg)', color: 'var(--info)', dot: 'var(--info)' },
    rejected: { bg: 'var(--danger-bg)', color: 'var(--danger)', dot: 'var(--danger)' },
    dispatched: { bg: 'var(--purple-bg)', color: 'var(--purple)', dot: 'var(--purple)' },
    draft: { bg: 'var(--surface2)', color: 'var(--muted)', dot: 'var(--muted2)' },
    inactive: { bg: 'var(--surface2)', color: 'var(--muted)', dot: 'var(--muted2)' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', background: s.bg, color: s.color
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {children || status}
    </span>
  );
};

/* ─── Chip ─── */
export const Chip = ({ children, variant = 'default' }) => {
  const styles = {
    default: { bg: 'var(--surface2)', color: 'var(--text2)', border: 'var(--border)' },
    blue: { bg: 'var(--accent-light)', color: 'var(--accent)', border: 'var(--accent)' },
    orange: { bg: 'var(--orange-bg)', color: 'var(--orange)', border: 'var(--orange)' },
    purple: { bg: 'var(--purple-bg)', color: 'var(--purple)', border: 'var(--purple)' },
    green: { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-mid)' },
  };
  const s = styles[variant] || styles.default;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 5, fontSize: 11.5, fontWeight: 500,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`, marginRight: 3
    }}>
      {children}
    </span>
  );
};

/* ─── Btn ─── */
export const Btn = ({ children, variant = 'outline', size = 'md', onClick, disabled, style, type = 'button' }) => {
  const v = {
    primary: { bg: 'var(--accent)', color: '#fff', border: 'none', hov: 'var(--accent2)' },
    success: { bg: 'var(--success)', color: '#fff', border: 'none', hov: '#15803d' },
    danger: { bg: 'var(--danger)', color: '#fff', border: 'none', hov: '#b91c1c' },
    outline: { bg: 'transparent', color: 'var(--text2)', border: '1.5px solid var(--border)', hov: 'var(--surface2)' },
    ghost: { bg: 'transparent', color: 'var(--text2)', border: 'none', hov: 'var(--surface2)' },
    warning: { bg: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid var(--warning-mid)', hov: '#fde68a' },
  }[variant] || {};
  const sz = { md: '7px 14px', sm: '5px 10px', xs: '3px 8px' }[size] || '7px 14px';
  const fs = { md: 13, sm: 12, xs: 11 }[size] || 13;
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: sz, borderRadius: 8, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all .15s',
        whiteSpace: 'nowrap', background: v.bg, color: v.color, border: v.border, opacity: disabled ? .5 : 1,
        fontSize: fs, ...style
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = v.hov; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = v.bg; }}
    >{children}</button>
  );
};

/* ─── Card ─── */
export const Card = ({ children, style }) => (
  <div style={{
    background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow)', overflow: 'hidden', ...style
  }}>
    {children}
  </div>
);

export const CardHeader = ({ children }) => (
  <div style={{
    padding: '13px 18px', borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    flexWrap: 'wrap'
  }}>
    {children}
  </div>
);

export const CardTitle = ({ children }) => (
  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{children}</div>
);

/* ─── InfoBanner ─── */
export const InfoBanner = ({ children, variant = 'blue' }) => {
  const v = {
    blue: { bg: 'var(--accent-light)', border: '#bfdbfe', color: '#1d4ed8' },
    warn: { bg: 'var(--warning-bg)', border: 'var(--warning-mid)', color: '#92400e' },
    danger: { bg: 'var(--danger-bg)', border: 'var(--danger-mid)', color: 'var(--danger)' },
    success: { bg: 'var(--success-bg)', border: 'var(--success-mid)', color: 'var(--success)' },
  }[variant] || {};
  return (
    <div style={{
      background: v.bg, border: `1px solid ${v.border}`, borderRadius: 8, padding: '10px 14px',
      fontSize: 12.5, color: v.color, fontWeight: 500, display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14
    }}>
      {children}
    </div>
  );
};

/* ─── Form helpers ─── */
export const FormGrid = ({ children, cols = 2, style }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 14, ...style }}>{children}</div>
);

export const FormGroup = ({ label, required, hint, error, children, full, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: full ? '1/-1' : undefined, ...style }}>
    {label && (
      <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}
      </label>
    )}
    {children}
    {hint && <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{hint}</span>}
    {error && <span style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>⚠ {error}</span>}
  </div>
);

export const Input = ({ style, ...props }) => (
  <input {...props} style={{ ...fieldBase, ...style }} onFocus={onFocus} onBlur={onBlur} />
);

export const CustomSelect = ({ children, style, ...props }) => (
  <select {...props} style={{ ...fieldBase, ...style }} onFocus={onFocus} onBlur={onBlur}>{children}</select>
);

export const Textarea = ({ style, ...props }) => (
  <textarea {...props} style={{ ...fieldBase, resize: 'vertical', minHeight: 72, ...style }} onFocus={onFocus} onBlur={onBlur} />
);

/* ─── Modal ─── */
export const Modal = ({ open, onClose, title, subtitle, icon, iconBg, children, footer, wide }) => {
  if (!open) return null;
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
      }}>
      <div style={{
        background: 'var(--bg)', borderRadius: 14, width: wide ? 700 : 490, maxWidth: '95vw',
        maxHeight: '90vh', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex',
        flexDirection: 'column', animation: 'modalIn .2s ease', border: '1px solid var(--border)'
      }}>
        {/* Header */}
        <div style={{ padding: '15px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon && (
            <div style={{
              width: 34, height: 34, borderRadius: 8, background: iconBg || 'var(--accent-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0
            }}>
              {icon}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 6, background: 'var(--surface2)',
            border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--muted)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', lineHeight: 1, fontFamily: 'inherit'
          }}>×</button>
        </div>
        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px 8px' }}>{children}</div>
        {/* Footer */}
        {footer && (
          <div style={{
            padding: '13px 20px', borderTop: '1px solid var(--border)',
            display: 'flex', justifyContent: 'flex-end', gap: 8
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Toast ─── */
export const Toast = ({ show, msg, type }) => {
  if (!show) return null;
  const bg = { success: 'var(--success)', error: 'var(--danger)', '': 'var(--text)' }[type] ?? 'var(--text)';
  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 9999, background: bg, color: '#fff',
      padding: '11px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: 'var(--shadow-lg)',
      display: 'flex', alignItems: 'center', gap: 8, maxWidth: 320,
      animation: 'modalIn .2s ease'
    }}>
      {msg}
    </div>
  );
};

/* ─── Table ─── */
export const Table = ({ headers, children, empty, noScroll, tableStyle, widths }) => (
  <div style={{ overflowX: noScroll ? 'visible' : 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: widths ? 'fixed' : undefined, ...tableStyle }}>
      {widths && (
        <colgroup>
          {widths.map((w, i) => <col key={i} style={{ width: w }} />)}
        </colgroup>
      )}
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{
              padding: '10px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.8px', color: 'var(--muted)', background: 'var(--surface2)', textAlign: 'left',
              whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)'
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {empty && (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)' }}>{empty}</div>
      </div>
    )}
  </div>
);

export const Tr = ({ children, highlight }) => (
  <tr style={{ background: highlight ? 'var(--success-bg)' : undefined, transition: 'background .1s' }}
    onMouseEnter={e => { if (!highlight) e.currentTarget.style.background = 'var(--surface-hover)'; }}
    onMouseLeave={e => { if (!highlight) e.currentTarget.style.background = ''; }}>
    {children}
  </tr>
);

export const Td = ({ children, strong, mono, accent, center, muted, style }) => (
  <td style={{
    padding: '11px 16px', fontSize: 13, borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    color: accent ? 'var(--accent)' : strong ? 'var(--text)' : muted ? 'var(--muted)' : 'var(--text2)',
    fontWeight: strong ? 600 : 400,
    fontFamily: mono ? "'JetBrains Mono',monospace" : undefined,
    textAlign: center ? 'center' : undefined, ...style
  }}>
    {children}
  </td>
);

/* ─── StatCard ─── */
export const StatCard = ({ icon, iconBg, value, valueColor, label, change, changeType }) => {
  const ct = { up: { bg: 'var(--success-bg)', color: 'var(--success)' }, down: { bg: 'var(--danger-bg)', color: 'var(--danger)' }, neutral: { bg: 'var(--surface2)', color: 'var(--muted)' } }[changeType] || {};
  return (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      padding: '18px 20px', boxShadow: 'var(--shadow)', transition: 'box-shadow .2s, transform .2s', cursor: 'default'
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.transform = ''; }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 9, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{icon}</div>
        {change && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 7px', borderRadius: 20, background: ct.bg, color: ct.color }}>{change}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.5px', lineHeight: 1, marginBottom: 4, color: valueColor || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</div>
    </div>
  );
};

/* ─── SectionHead ─── */
export const SectionHead = ({ title, subtitle, children }) => (
  <div className="section-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
    <div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', letterSpacing: '-.4px' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{subtitle}</div>}
    </div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>{children}</div>
  </div>
);

/* ─── DetailField ─── */
export const DataField = ({ label, value, accent }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: accent ? 'var(--accent)' : 'var(--text)', marginTop: 2 }}>{value || ''}</div>
  </div>
);
